import { FormInstance } from "antd";
import { SorterResult } from "antd/es/table/interface";
import { PartnerManagementRequestModel } from "api/repositories/partnerManagement/model/PartnerManagementRequestModel";
import { PartnerManagementResponseModel } from "api/repositories/partnerManagement/model/PartnerManagementResponseModel";
import {
  Error,
  Paging,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseViewModel } from "app/based/baseViewModel/BaseViewModel";
import { BalanceHistoryInjection } from "features/balanceHistory/di/BalanceHistoryInjection";
import { BalanceHistoryActionEntity } from "features/balanceHistory/domain/entities/BalanceHistoryActionEntity";
import { BalanceHistoryDetailEntity } from "features/balanceHistory/domain/entities/BalanceHistoryDetailEntity";
import { BalanceHistoryRequestEntity } from "features/balanceHistory/domain/entities/BalanceHistoryRequestEntity";
import { BalanceHistoryResponseEntity } from "features/balanceHistory/domain/entities/BalanceHistoryResponseEntity";
import { TransactionForRefundRequestEntity } from "features/balanceHistory/domain/entities/TransactionForRefundRequestEntity";
import { TransactionForRefundResponseEntity } from "features/balanceHistory/domain/entities/TransactionForRefundResponseEntity";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { getMessage } from "utils/helpersInTs/helpersInTs";
import { strings } from "utils/localizedStrings";

class BalanceHistoryInput implements Disposable {
  public loading = new BehaviorSubject<boolean>(false);
  public actionLoadng = new BehaviorSubject<boolean>(false);
  public refundLoading = new BehaviorSubject<boolean>(false);
  public partnerLoading = new BehaviorSubject<boolean>(false);
  public fetchHistory = new BehaviorSubject<BalanceHistoryRequestEntity>({});
  public userLoading = new BehaviorSubject<boolean>(false);
  public fetchHistoryForRefund =
    new BehaviorSubject<TransactionForRefundRequestEntity>(
      new TransactionForRefundRequestEntity(),
    );
  public fetchDetail = new BehaviorSubject<{ code?: string }>({});
  public fetchUser = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchFilterUser = new BehaviorSubject<BaseUserRequestModel>({});
  public fetchPartner = new BehaviorSubject<PartnerManagementRequestModel>({});
  public fetchActions = new BehaviorSubject<{
    value?: BalanceHistoryActionEntity;
    type?: "create" | "update";
  }>({});

  [Symbol.dispose](): void {
    this.loading.complete();
    this.actionLoadng.complete();
    this.refundLoading.complete();
    this.partnerLoading.complete();
    this.fetchHistory.complete();
    this.fetchDetail.complete();
    this.fetchUser.complete();
    this.fetchFilterUser.complete();
    this.fetchHistoryForRefund.complete();
    this.fetchPartner.complete();
    this.userLoading.complete();
    this.fetchActions.complete();
  }
}

class BalanceHistoryOutput implements Disposable {
  public savedHistory = new BehaviorSubject<
    Array<BalanceHistoryResponseEntity>
  >([]);
  public savedDetail = new BehaviorSubject<BalanceHistoryDetailEntity>({});
  public savedActionUserList = new BehaviorSubject<
    Array<BaseUserResponseModel>
  >([]);
  public savedFilterUserList = new BehaviorSubject<
    Array<BaseUserResponseModel>
  >([]);
  public savedTransListForRefund = new BehaviorSubject<
    Array<TransactionForRefundResponseEntity>
  >([]);
  public savedPartnerList = new BehaviorSubject<
    Array<PartnerManagementResponseModel>
  >([]);
  public pagination = new BehaviorSubject<Paging>({});
  public refundPagination = new BehaviorSubject<Paging>({});
  public userPaging = new BehaviorSubject<Paging>({});
  public partnerPaging = new BehaviorSubject<Paging>({});
  public onError = new BehaviorSubject<Error>({});
  public createUpdateTickerSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.savedHistory.complete();
    this.savedDetail.complete();
    this.savedActionUserList.complete();
    this.savedFilterUserList.complete();
    this.savedPartnerList.complete();
    this.savedTransListForRefund.complete();
    this.pagination.complete();
    this.refundPagination.complete();
    this.userPaging.complete();
    this.partnerPaging.complete();
    this.onError.complete();
    this.createUpdateTickerSuccess.complete();
  }
}

@injectable()
export class BalanceHistoryViewModel extends BaseViewModel<
  BalanceHistoryInput,
  BalanceHistoryOutput
> {
  constructor() {
    super(new BalanceHistoryInput(), new BalanceHistoryOutput());
  }
  private balanceHistoryUseCase =
    BalanceHistoryInjection.getBalanceHistoryUseCase();

  public buildTableQuery(
    pagination: any,
    sorter: SorterResult,
    filterForm: React.RefObject<FormInstance<any>>,
    prevParams: BalanceHistoryRequestEntity,
  ) {
    const formValues = filterForm?.current?.getFieldsValue(true) || {};
    const { created, ...rest } = formValues;

    const order =
      sorter?.order === "descend"
        ? `${String(sorter?.column?.dataIndex)} DESC`
        : sorter?.order === "ascend"
          ? `${String(sorter?.column?.dataIndex)} ASC`
          : `createdAt DESC`;

    const query = {
      ...prevParams,
      ...rest,
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
      order,
    };

    const paginationState = {
      page: pagination?.current - 1,
      limit: pagination?.pageSize,
    };

    return {
      query,
      pagination: paginationState,
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchHistory, async (params) => {
      try {
        this.output.savedHistory.next([]);
        this.input.loading.next(true);
        const res =
          await this.balanceHistoryUseCase.getBalanceHistoryList(params);
        if (res?.data) {
          this.output.savedHistory.next(res?.data);
          this.output.pagination.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.savedHistory.next([]);
        this.output.pagination.next({});
        this.output.onError.next(error);
      } finally {
        this.input.loading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchHistoryForRefund, async (params) => {
      try {
        if (params?.limit === 0) {
          this.output.savedTransListForRefund.next([]);
          this.output.refundPagination.next({
            page: 0,
            limit: 20,
            total: 0,
          });
        } else {
          this.input.refundLoading.next(true);
          const res =
            await this.balanceHistoryUseCase.getTransactionForRefund(params);
          if (res?.data) {
            this.output.savedTransListForRefund.next(res?.data);
            this.output.refundPagination.next(res?.pagination);
          }
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.refundLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.output.savedDetail.next({});
        this.input.actionLoadng.next(true);
        const res = await this.balanceHistoryUseCase.getDetail({
          code: params?.code,
        });
        if (res?.data) {
          this.output.savedDetail.next(res?.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.actionLoadng.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchUser, async (params) => {
      try {
        this.input.userLoading.next(true);
        const res = await this.balanceHistoryUseCase.getStaffList(params);
        if (res) {
          const nextData = res?.data ?? [];
          const currentData =
            params?.page && params?.page > 0
              ? this.output.savedActionUserList.getValue()
              : [];
          this.output.savedActionUserList.next([...currentData, ...nextData]);
          this.output.userPaging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
      } finally {
        this.input.userLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchFilterUser, async (params) => {
      try {
        const res = await this.balanceHistoryUseCase.getStaffList(params);
        if (res) {
          this.output.savedFilterUserList.next(res?.data ?? []);
        }
      } catch (error) {
        console.error(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPartner, async (params) => {
      try {
        this.input.partnerLoading.next(true);
        const res = await this.balanceHistoryUseCase.getPartnerList(params);
        if (res) {
          const nextData = res?.data ?? [];
          const currentData =
            params?.page && params?.page > 0
              ? this.output.savedPartnerList.getValue()
              : [];
          this.output.savedPartnerList.next([...currentData, ...nextData]);
          this.output.partnerPaging.next(res?.pagination);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.input.partnerLoading.next(false);
      }
    });

    this.skipFirstInitial(this.input.fetchActions, async (props) => {
      const { type, value } = props;
      switch (type) {
        case "create":
          let body = value as BalanceHistoryActionEntity;
          try {
            this.input.actionLoadng.next(true);
            const res =
              await this.balanceHistoryUseCase?.createBalanceTicket(body);
            if (res) {
              getMessage(
                strings.BalanceHistory.Message.CreateSuccessfully,
                4,
                "success",
              );
              this.output.createUpdateTickerSuccess.next(true);
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.actionLoadng.next(false);
          }
          break;
        case "update":
          let update = value as BalanceHistoryActionEntity;
          try {
            this.input.actionLoadng.next(true);
            const res =
              await this.balanceHistoryUseCase?.updateBalanceTicket(update);
            if (res) {
              switch (update?.status) {
                case 1:
                  getMessage(
                    strings.BalanceHistory.Message.ApproveSuccessfully,
                    4,
                    "success",
                  );
                  this.output.createUpdateTickerSuccess.next(true);
                  break;
                case 2:
                  getMessage(
                    strings.BalanceHistory.Message.RejectSuccessfully,
                    4,
                    "success",
                  );
                  this.output.createUpdateTickerSuccess.next(true);
              }
            }
          } catch (error) {
            console.error(error);
            this.output.onError.next(error);
          } finally {
            this.input.actionLoadng.next(false);
          }
      }
    });
  }
}
